import {
  cn,
  type PaginationState,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import { SubscriptionTab, UsageTab, useSubscriptionData } from "@modules";
import { useUserInfo } from "@query";
import { APP_BASE_PATH } from "@shared";
import { cva } from "class-variance-authority";
import { useCallback, useState } from "react";

const subscriptionPageLayoutVariants = cva(
  "flex min-h-0 flex-1 flex-col overflow-hidden",
);

export function SubscriptionPage() {
  const { t } = useTranslation();
  const { data: userInfo } = useUserInfo(APP_BASE_PATH);
  const companyUuid = userInfo?.companyUuid;

  const [activeTab, setActiveTab] = useState("subscription");
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const handleTabChange = (val: string) => {
    setActiveTab(val);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };
  const handlePaginationChange = useCallback(
    (
      updater: PaginationState | ((old: PaginationState) => PaginationState),
    ) => {
      setPagination((prev) =>
        typeof updater === "function" ? updater(prev) : updater,
      );
    },
    [],
  );
  const {
    subscriptionData,
    subscriptionTotalElements,
    subscriptionTotalPages,
    isSubscriptionsLoading,
    usageData,
    usageTotalElements,
    usageTotalPages,
    isUsageLoading,
  } = useSubscriptionData(companyUuid, {
    page: pagination.pageIndex,
    pageSize: pagination.pageSize,
  });

  return (
    <div className={cn(subscriptionPageLayoutVariants(), "h-full")}>
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="flex flex-col h-full min-h-0">
        <TabsList variant="line" size="md" className="shrink-0">
          <TabsTrigger value="subscription">
            {t("hub.home.solution_subscription_status")}
          </TabsTrigger>
          <TabsTrigger value="usage">{t("hub.subscription.usage")}</TabsTrigger>
        </TabsList>

        <TabsContent
          value="subscription"
          forceMount
          hidden={activeTab !== "subscription"}
          className="flex-1 min-h-0 h-full flex flex-col">
          <SubscriptionTab
            data={subscriptionData}
            totalElements={subscriptionTotalElements}
            totalPages={subscriptionTotalPages}
            pagination={pagination}
            onPaginationChange={handlePaginationChange}
            loading={isSubscriptionsLoading}
          />
        </TabsContent>

        <TabsContent
          value="usage"
          forceMount
          hidden={activeTab !== "usage"}
          className="flex-1 min-h-0 h-full flex flex-col">
          <UsageTab
            data={usageData}
            totalElements={usageTotalElements}
            totalPages={usageTotalPages}
            pagination={pagination}
            onPaginationChange={handlePaginationChange}
            loading={isUsageLoading}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
